import { type ReactNode, useMemo } from 'react'
import { Button, Collapse, Drawer, Message } from '@arco-design/web-react'
import { IconCopy } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import type { LogEntry } from '@/types'
import LogTimeline from '@/components/log/LogTimeline'
import { parseLogOther } from '@/utils/parseLogOther'
import { renderQuota, formatNumberCompact, formatMicroUsd } from '@/utils/quota'
import {
  renderDurationMs,
  renderErrorStage,
  renderLogStatus,
  renderModelTag,
  renderStreamMode,
  renderTimestamp,
} from '@/utils/render'
import { drawerWidth } from '@/utils/responsive'

function DetailRow({
  label,
  children,
  onCopy,
  copyLabel,
}: {
  label: string
  children: ReactNode
  onCopy?: () => void
  copyLabel?: string
}) {
  return (
    <div>
      <div className="text-xs text-[var(--console-text-subtle)] mb-0.5">{label}</div>
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1 break-all text-[var(--console-text-body)]">{children}</div>
        {onCopy ? (
          <Button
            type="text"
            size="mini"
            icon={<IconCopy />}
            aria-label={copyLabel}
            onClick={onCopy}
            className="flex-shrink-0"
          />
        ) : null}
      </div>
    </div>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-[13px] font-semibold text-[var(--console-text-h)] mb-3 pb-2 border-b border-[var(--console-border)]">
      {children}
    </h3>
  )
}

interface LogDetailDrawerProps {
  visible: boolean
  record: LogEntry | null
  isAdmin: boolean
  showUser: boolean
  onClose: () => void
}

export default function LogDetailDrawer({
  visible,
  record,
  isAdmin,
  showUser,
  onClose,
}: LogDetailDrawerProps) {
  const { t } = useTranslation()
  const parsed = useMemo(() => (record ? parseLogOther(record.otherInfo) : null), [record])

  const handleCopy = (text: string) => {
    void navigator.clipboard.writeText(text).then(() => Message.success(t('复制成功')))
  }

  if (!record) return null

  const requestId = record.requestId || parsed?.trace?.requestId
  const errorHint = (() => {
    const stage = record.errorStage || parsed?.error?.stage
    if (!stage) return undefined
    return t(`tt.log.errorHint.${stage}`, { defaultValue: t('tt.log.errorHint.default') })
  })()

  return (
    <Drawer
      title={t('tt.log.detailTitle')}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={drawerWidth(720)}
      unmountOnExit
    >
      <div className="space-y-6 text-sm">
        <div>{renderLogStatus(record.status)}</div>

        <div>
          <SectionTitle>{t('tt.log.sectionOverview')}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailRow
              label={t('tt.log.requestId')}
              onCopy={requestId ? () => handleCopy(requestId) : undefined}
              copyLabel={t('复制')}
            >
              <code className="text-xs font-mono">{requestId || '-'}</code>
            </DetailRow>
            <DetailRow label={t('tt.log.time')}>{renderTimestamp(record.createdAt)}</DetailRow>
            <DetailRow label={t('tt.log.model')}>{record.modelName ? renderModelTag(record.modelName) : '-'}</DetailRow>
            {showUser && record.username ? (
              <DetailRow label={t('tt.log.user')}>{record.username}</DetailRow>
            ) : null}
            <DetailRow label={t('tt.log.token')}>{record.tokenName || '-'}</DetailRow>
            <DetailRow label={t('tt.log.returnMode')}>{renderStreamMode(record.stream ?? record.isStream)}</DetailRow>
          </div>
        </div>

        <div>
          <SectionTitle>{t('tt.log.sectionTiming')}</SectionTitle>
          <LogTimeline record={record} isAdmin={isAdmin} />
        </div>

        <div>
          <SectionTitle>{t('tt.log.sectionBilling')}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailRow label={t('tt.log.promptTokens')}>{formatNumberCompact(record.promptTokens)}</DetailRow>
            <DetailRow label={t('tt.log.completionTokens')}>{formatNumberCompact(record.completionTokens)}</DetailRow>
            <DetailRow label={t('tt.log.quota')}>{renderQuota(record.quota)}</DetailRow>
            <DetailRow label={t('tt.log.totalMs')}>{renderDurationMs(record.totalMs ?? parsed?.timing?.totalMs)}</DetailRow>
            {parsed?.billing?.modelRatio != null ? (
              <DetailRow label={t('tt.log.modelRatio')}>{parsed.billing.modelRatio}</DetailRow>
            ) : null}
            {parsed?.billing?.groupRatio != null ? (
              <DetailRow label={t('tt.log.groupRatio')}>{parsed.billing.groupRatio}</DetailRow>
            ) : null}
            {parsed?.billing?.preConsumedQuota != null ? (
              <DetailRow label={t('tt.log.preConsumed')}>{renderQuota(parsed.billing.preConsumedQuota)}</DetailRow>
            ) : null}
            {parsed?.billing?.actualQuota != null ? (
              <DetailRow label={t('tt.log.actualQuota')}>{renderQuota(parsed.billing.actualQuota)}</DetailRow>
            ) : null}
            {parsed?.billing?.refundQuota != null ? (
              <DetailRow label={t('tt.log.refundQuota')}>{renderQuota(parsed.billing.refundQuota)}</DetailRow>
            ) : null}
            {isAdmin && parsed?.billing?.userCostMicroUsd != null ? (
              <DetailRow label={t('tt.log.userCost')}>
                {formatMicroUsd(parsed.billing.userCostMicroUsd)}
              </DetailRow>
            ) : null}
            {isAdmin && parsed?.billing?.channelCostMicroUsd != null ? (
              <DetailRow label={t('tt.log.channelCost')}>
                <span>{formatMicroUsd(parsed.billing.channelCostMicroUsd)}</span>
                {parsed.billing.channelCostSource ? (
                  <span className="ml-2 text-xs text-[var(--console-text-subtle)]">
                    ({t(`tt.log.channelCostSource.${parsed.billing.channelCostSource}`, {
                      defaultValue: parsed.billing.channelCostSource,
                    })})
                  </span>
                ) : null}
              </DetailRow>
            ) : null}
          </div>
        </div>

        {isAdmin ? (
          <div>
            <SectionTitle>{t('tt.log.sectionRoute')}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailRow label={t('tt.log.channelId')}>
                {record.channel != null && record.channel > 0 ? `#${record.channel}` : '-'}
              </DetailRow>
              <DetailRow label={t('tt.log.channel')}>{record.channelName || parsed?.route?.channelName || '-'}</DetailRow>
              <DetailRow label={t('tt.log.group')}>{record.group || parsed?.route?.usingGroup || '-'}</DetailRow>
              <DetailRow label={t('tt.log.upstreamModel')}>{parsed?.route?.upstreamModel || '-'}</DetailRow>
              <DetailRow label={t('tt.log.retryCount')}>{record.retryCount ?? parsed?.route?.retryCount ?? '-'}</DetailRow>
              {record.upstreamRequestId || parsed?.trace?.upstreamRequestId ? (
                <DetailRow
                  label={t('tt.log.upstreamRequestId')}
                  onCopy={() =>
                    handleCopy(String(record.upstreamRequestId || parsed?.trace?.upstreamRequestId))
                  }
                  copyLabel={t('复制')}
                >
                  <code className="text-xs font-mono">
                    {record.upstreamRequestId || parsed?.trace?.upstreamRequestId}
                  </code>
                </DetailRow>
              ) : null}
            </div>
          </div>
        ) : null}

        {(record.errorStage || parsed?.error?.stage || record.content) && (
          <div>
            <SectionTitle>{t('tt.log.sectionError')}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailRow label={t('tt.log.errorStage')}>
                {renderErrorStage(record.errorStage || parsed?.error?.stage)}
              </DetailRow>
              <DetailRow label={t('tt.log.errorCode')}>{record.errorCode || parsed?.error?.code || '-'}</DetailRow>
              {isAdmin ? (
                <DetailRow label={t('tt.log.upstreamStatus')}>
                  {record.upstreamStatusCode ?? parsed?.error?.upstreamStatus ?? '-'}
                </DetailRow>
              ) : null}
              <DetailRow label={t('tt.log.streamStatus')}>{parsed?.stream?.status || '-'}</DetailRow>
              {parsed?.stream?.chunks != null ? (
                <DetailRow label={t('tt.log.chunkCount', { defaultValue: 'Chunk 数' })}>{parsed.stream.chunks}</DetailRow>
              ) : null}
              {parsed?.stream?.clientDisconnected ? (
                <DetailRow label={t('tt.log.clientDisconnected', { defaultValue: '客户端断开' })}>{t('是')}</DetailRow>
              ) : null}
              {parsed?.stream?.upstreamInterrupted ? (
                <DetailRow label={t('tt.log.upstreamInterrupted', { defaultValue: '上游中断' })}>{t('是')}</DetailRow>
              ) : null}
              {parsed?.stream?.usageMissing ? (
                <DetailRow label={t('tt.log.usageMissing', { defaultValue: 'Usage 缺失' })}>{t('是')}</DetailRow>
              ) : null}
              <div className="sm:col-span-2">
                <DetailRow label={t('tt.log.content')}>{record.content || parsed?.error?.message || '-'}</DetailRow>
              </div>
              {errorHint ? (
                <div className="sm:col-span-2 text-xs text-[var(--console-text-subtle)]">{errorHint}</div>
              ) : null}
            </div>
          </div>
        )}

        {isAdmin && record.otherInfo ? (
          <div>
            <SectionTitle>{t('tt.log.sectionRaw')}</SectionTitle>
            <Collapse bordered={false} defaultActiveKey={[]}>
              <Collapse.Item header={t('tt.log.rawJson')} name="raw">
                <pre className="text-xs whitespace-pre-wrap break-all bg-[var(--console-hover-row)] p-3 rounded-md max-h-64 overflow-auto">
                  {record.otherInfo}
                </pre>
              </Collapse.Item>
            </Collapse>
          </div>
        ) : null}
      </div>
    </Drawer>
  )
}
